import React, { useState } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import * as Yup from 'yup';
import {
    moneyFormat,
    timeConverter,
    toGregorian,
    toSelectOptions,
} from 'utils/utils';
import { TransationStatus } from './utils';
import { pspLabel } from 'utils/psp';
import TransactionStatusBullet from 'features/transactions/components/transaction-status-bullet';
import { useGetTransactionListQuery } from 'app/store';
import type { SearchJsonObject } from 'components/search-field/search.form';
import { useGateway } from 'hooks';

const PER_PAGE_COUNT = 15;

export type TransactionSearch = {
    from_date?: string;
    to_date?: string;
} & Record<string, unknown>;

export const getTransactionApiSearch = (search: TransactionSearch) => {
    const { from_date, to_date, ...rest } = search;

    return {
        ...rest,
        ...(typeof search.card === 'string'
            ? { card: search.card.replace(/\s/g, '') }
            : {}),
        ...(typeof from_date === 'string' && from_date
            ? { from_date: toGregorian(from_date) }
            : {}),
        ...(typeof to_date === 'string' && to_date
            ? { to_date: toGregorian(to_date) }
            : {}),
    };
};

// hook for fetch list
export function useListData({
    limitCount,
    enabled = true,
}: {
    limitCount?: number;
    enabled?: boolean;
} = {}) {
    const active = useGateway();

    const [search, setSearch] = useState<TransactionSearch>({});
    const [page, setPage] = useState<number>(1);

    const {
        data,
        isFetching: isLoadingData,
        isSuccess: isSuccessData,
        refetch,
    } = useGetTransactionListQuery(
        {
            workspaceId: active?.id as number,
            page: page,
            perPage: PER_PAGE_COUNT,
            search: getTransactionApiSearch(search),
        },
        { skip: !enabled || active?.status !== 'activated' },
    );

    const rowsData = data?.items ?? [];

    return {
        rowsData: limitCount ? rowsData.slice(0, limitCount) : rowsData,
        isLoadingData,
        isSuccessData,
        search,
        setSearch,
        page,
        perPage: PER_PAGE_COUNT,
        setPage,
        paginateInfo:
            limitCount && limitCount < PER_PAGE_COUNT
                ? undefined
                : data?.pagination,
        refetch,
    };
}

// data table's fields
export const transactionColumns: GridColDef[] = [
    {
        field: 'id',
        headerName: '#',
        width: 68,
    },
    {
        field: 'tracking_code',
        headerName: 'شناسه تراکنش',
        minWidth: 145,
        cellClassName: 'ltr-cell',
    },
    {
        field: 'amount',
        headerName: 'مبلغ (ريال)',
        minWidth: 125,
        renderCell: ({ row }) => moneyFormat(row.amount),
    },
    {
        field: 'fee',
        headerName: 'کارمزد (ريال)',
        minWidth: 125,
        renderCell: ({ row }) => {
            const systemFee = Number(row?.profit?.system?.amount ?? 0);
            const shaparakFee = Number(row?.profit?.shaparak?.amount ?? 0);

            return moneyFormat(systemFee + shaparakFee);
        },
    },
    {
        field: 'order_number',
        headerName: 'شماره سفارش',
        minWidth: 135,
        cellClassName: 'ltr-cell',
        renderCell: ({ row }) => row?.detail?.invoice_id ?? '',
    },
    {
        field: 'card_number',
        headerName: 'شماره کارت',
        minWidth: 170,
        renderCell: ({ row }) => {
            const card = row?.detail?.card;
            if (!card) return '';

            return (
                <span
                    dir="ltr"
                    style={{
                        direction: 'ltr',
                        unicodeBidi: 'isolate',
                        display: 'inline-block',
                        width: '100%',
                    }}
                >
                    {String(card)}
                </span>
            );
        },
    },
    {
        field: 'created_at',
        headerName: 'زمان تراکنش',
        minWidth: 160,
        renderCell: ({ row }) => timeConverter(row.created_at),
    },
    {
        field: 'status',
        headerName: 'وضعیت',
        minWidth: 135,
        renderCell: ({ row }) => (
            <TransactionStatusBullet status={row?.status} />
        ),
    },
];

const digitsOnlyInput = (maxLength?: number) => ({
    inputMode: 'numeric' as const,
    onInput: (event: React.FormEvent<HTMLInputElement>) => {
        const input = event.currentTarget;
        const normalized = input.value.replace(/[^0-9]/g, '');
        input.value = maxLength ? normalized.slice(0, maxLength) : normalized;
    },
});

const englishAlphaNumericInput = (maxLength: number) => ({
    inputMode: 'text' as const,
    onInput: (event: React.FormEvent<HTMLInputElement>) => {
        const input = event.currentTarget;
        input.value = input.value
            .replace(/[^A-Za-z0-9]/g, '')
            .slice(0, maxLength);
    },
});

const cardSearchInput = {
    inputMode: 'text' as const,
    onInput: (event: React.FormEvent<HTMLInputElement>) => {
        const input = event.currentTarget;
        input.value = input.value.replace(/[^0-9*]/g, '').slice(0, 16);
    },
};

export const transactionSearchJson: SearchJsonObject = {
    fields: [
        {
            name: 'id',
            label: 'شماره تراکنش',
            component: 'Text',
            ltr: 'true',
            icon: <i className="fal fa-hashtag"></i>,
            maxLength: 15,
            inputProps: digitsOnlyInput(15),
            gridSize: 6,
        },
        {
            name: 'tracking_code',
            label: 'شناسه تراکنش',
            component: 'Text',
            ltr: 'true',
            icon: <i className="fa-light fa-fingerprint"></i>,
            maxLength: 15,
            inputProps: englishAlphaNumericInput(15),
            gridSize: 6,
        },
        {
            name: 'card',
            label: 'شماره کارت',
            component: 'Text',
            ltr: 'true',
            icon: <i className="fa-light fa-credit-card"></i>,
            maxLength: 16,
            inputProps: cardSearchInput,
            gridSize: 6,
        },
        {
            name: 'refrence_code',
            label: 'شماره ارجاع بانکی',
            component: 'Text',
            ltr: 'true',
            icon: <i className="fal fa-arrows-rotate"></i>,
            gridSize: 6,
        },
        {
            name: 'invoice_id',
            label: 'شماره فاکتور',
            component: 'Text',
            ltr: 'true',
            icon: <i className="fa-light fa-file-invoice"></i>,
            gridSize: 6,
        },
        {
            name: 'status',
            label: 'وضعیت تراکنش',
            component: 'Select',
            icon: <i className="fa-light fa-circle-check"></i>,
            options: toSelectOptions(TransationStatus),
            gridSize: 6,
        },
        {
            name: 'from_amount',
            label: 'از مبلغ',
            component: 'Text',
            ltr: 'true',
            icon: <i className="fa-light fa-coins"></i>,
            inputProps: digitsOnlyInput(),
            formatThousands: true,
            secondIcon: <span style={{ marginRight: '-5px' }}>ریال</span>,
            gridSize: 6,
        },
        {
            name: 'to_amount',
            label: 'تا مبلغ',
            component: 'Text',
            ltr: 'true',
            icon: <i className="fa-light fa-coins"></i>,
            inputProps: digitsOnlyInput(),
            formatThousands: true,
            secondIcon: <span style={{ marginRight: '-5px' }}>ریال</span>,
            gridSize: 6,
        },
        {
            name: 'from_date',
            label: 'از تاریخ',
            component: 'Date',
            icon: <i className="fa-light fa-calendar-days"></i>,
            startOnDay: true,
            readOnlyInput: true,
            gridSize: 6,
        },
        {
            name: 'to_date',
            label: 'تا تاریخ',
            component: 'Date',
            icon: <i className="fa-light fa-calendar-days"></i>,
            startOnDay: true,
            readOnlyInput: true,
            gridSize: 6,
        },
        {
            name: 'fullname',
            label: 'نام پرداخت کننده',
            component: 'Text',
            icon: <i className="fa-light fa-user"></i>,
            gridSize: 6,
        },
        {
            name: 'mobile',
            label: 'شماره همراه پرداخت کننده',
            component: 'Text',
            ltr: 'true',
            icon: <i className="fa-solid fa-phone"></i>,
            maxLength: 11,
            inputProps: digitsOnlyInput(11),
            gridSize: 6,
        },
        {
            name: 'email',
            label: 'ایمیل پرداخت کننده',
            component: 'Text',
            ltr: 'true',
            icon: <i className="fa-solid fa-at"></i>,
            gridSize: 6,
        },
        {
            name: 'psp',
            label: 'PSP',
            component: 'PSP',
            icon: <i className="fa-light fa-building-columns"></i>,
            options: toSelectOptions(pspLabel),
            gridSize: 6,
        },
    ],
    validation: Yup.object().shape({
        id: Yup.string()
            .nullable()
            .matches(/^\d{1,15}$/, {
                message: 'شماره تراکنش باید فقط عدد و حداکثر ۱۵ رقم باشد',
                excludeEmptyString: true,
            }),
        tracking_code: Yup.string()
            .nullable()
            .matches(/^[A-Za-z0-9]{1,15}$/, {
                message: 'شناسه تراکنش باید فقط شامل عدد و حروف انگلیسی و حداکثر ۱۵ کاراکتر باشد',
                excludeEmptyString: true,
            }),
        card: Yup.string()
            .nullable()
            .matches(/^[0-9*]{1,16}$/, {
                message: 'شماره کارت فقط می‌تواند شامل عدد و * و حداکثر ۱۶ کاراکتر باشد',
                excludeEmptyString: true,
            }),
        refrence_code: Yup.string().nullable(),
        invoice_id: Yup.string().nullable(),
        from_amount: Yup.number()
            .nullable()
            .transform((value, originalValue) =>
                originalValue === '' ? null : value,
            )
            .typeError('فیلد از مبلغ باید یک عدد باشد')
            .integer('فیلد از مبلغ باید عدد صحیح باشد')
            .min(0, 'مبلغ نمی‌تواند منفی باشد'),
        to_amount: Yup.number()
            .nullable()
            .transform((value, originalValue) =>
                originalValue === '' ? null : value,
            )
            .typeError('فیلد تا مبلغ باید یک عدد باشد')
            .integer('فیلد تا مبلغ باید عدد صحیح باشد')
            .min(0, 'مبلغ نمی‌تواند منفی باشد'),
        from_date: Yup.string().nullable(),
        to_date: Yup.string().nullable(),
        fullname: Yup.string().nullable(),
        mobile: Yup.string()
            .nullable()
            .matches(/^09\d{9}$/, {
                message: 'شماره همراه باید ۱۱ رقم و با 09 شروع شود',
                excludeEmptyString: true,
            }),
        email: Yup.string()
            .nullable()
            .email('فرمت ایمیل وارد شده صحیح نمی‌باشد'),
    }),
};
